<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OnClickOutside } from '@vueuse/components';
import { watchDebounced } from '@vueuse/core';
import axios from 'axios';
import { Search, X } from 'lucide-vue-next';
import {
    ComboboxAnchor,
    ComboboxInput,
    ComboboxPortal,
    ComboboxRoot,
} from 'radix-vue';
import { onMounted, PropType, ref } from 'vue';

const props = defineProps({
    pharmacyDrugs: {
        type: Array as PropType<string[]>,
        default: [],
    },
});

interface Drug {
    drugCode: string;
    drugName: string;
}

const drugs = ref<Drug[]>([]);
const modelValue = ref<string[]>([]);
const open = ref<boolean>(false);
const searchTerm = ref<string>('');
const isFetching = ref<boolean>(false);
const error = ref<boolean>(false);

async function fetchDrugs() {
    if (!searchTerm.value || searchTerm.value.length < 2) {
        drugs.value = [];
        return;
    }

    if (isFetching.value) {
        return;
    }

    isFetching.value = true;
    error.value = false;

    try {
        const currentSearchTerm = searchTerm.value;
        const res = await axios.post('/searchDrugs', { drug: searchTerm.value });

        if (currentSearchTerm !== searchTerm.value) {
            return;
        }

        drugs.value = Object.values(res.data);
    } catch (err) {
        error.value = true;
        drugs.value = [];
    } finally {
        isFetching.value = false;
    }
}

function selectDrug(drugName: string) {
    modelValue.value = [...modelValue.value, drugName];
    if (drugs.value.length === 0) {
        open.value = false;
    }
    setTimeout(() => {
        searchTerm.value = '';
    }, 0);
}

function removeDrug(drugName: string) {
    modelValue.value = modelValue.value.filter((i: string) => i !== drugName);
}

onMounted(() => {
    modelValue.value = props.pharmacyDrugs;
});

function onClickOutside() {
    open.value = false;
}

watchDebounced(
    searchTerm,
    () => {
        fetchDrugs();
    },
    { debounce: 300, maxWait: 1000 }
);
</script>

<template>
    <div>
        <Label class="font-semibold">
            Which medications do you need refilled?
        </Label>
        <OnClickOutside @trigger="onClickOutside">
            <ComboboxRoot :open="open"  @update:open="open = $event"
                @update:searchTerm="searchTerm = $event" class="w-full mt-1.5">
                <ComboboxAnchor as-child>
                    <ComboboxInput as-child>
                        <div class="relative">
                            <Input placeholder="Search for Medications" class="pl-8 pr-3" />
                            <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
                                <Search class="size-4 text-gray-800" />
                            </span>
                        </div>
                    </ComboboxInput>
                </ComboboxAnchor>

                <ComboboxPortal>
                    <CommandList position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
                        <div v-if="isFetching" class="p-3 py-4 text-center text-primary font-semibold text-sm animate-pulse">
                            Searching for medications...
                        </div>
                        <div v-else-if="error" class="p-3 text-center text-red-500">
                            Error searching for medications
                            <Button :disabled="isFetching" class="ml-3" @click="fetchDrugs">
                                Try again
                            </Button>
                        </div>
                        <CommandEmpty v-else />
                        <CommandGroup>
                            <CommandItem v-for="drug in drugs" :key="drug.drugCode" :value="drug.drugName" class="py-2.5 cursor-pointer"
                                @click.prevent="selectDrug(drug.drugName)">
                                {{ drug.drugName }}
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </ComboboxPortal>
            </ComboboxRoot>
        </OnClickOutside>
        <FormField name="drugs" v-model="modelValue">
            <FormItem>
                <FormControl>
                    <div v-if="modelValue.length" class="flex flex-wrap mt-4 w-full select-none">
                        <div v-for="drug in modelValue" :key="drug"
                            class="flex items-center border first:rounded-t-xl last:rounded-b-xl overflow-hidden border-b-0 last:border-b grow-0 w-full justify-between !bg-white">
                            <span class="pr-2 pl-3 py-3 text-gray-800 font-medium text-md grow text-balance max-w-[85%] break-words">{{ drug }}</span>
                            <div @click="removeDrug(drug)"
                                class="flex items-center rounded-none border-l px-2 h-full cursor-pointer basis-12 shrink-0 justify-center grow-0 active:bg-gray-100">
                                <X class="w-5 h-5 text-gray-500" />
                            </div>
                        </div>
                    </div>
                </FormControl>
            </FormItem>
        </FormField>
    </div>
</template>
